import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { MAX_MEDIA_BYTES } from '../shared/client-runtime';
import type {
  Ack,
  ClientEvents,
  ControllerRoomState,
  InviteState,
  PendingDeviceSummary,
  PublicRoomState,
  RoomMode,
  RoomClosed,
  ScreenIdentify,
  ScreenRemoved,
  SceneName,
  ServerEvents,
  SessionResumeState,
  TileLayout
} from '../shared/protocol';
import { MosaicMasthead, SiteControls } from './SiteControls';
import { startOrientationSensor, type OrientationSensorStatus } from './device-orientation';
import { readViewport } from './device';
import { mosaicCopy, readLocale, type Locale } from './i18n';
import { deleteSession, postSession, uploadSessionFile } from './session-api';
import { request } from './socket-request';
import { inviteTokenFromHash, publicInviteUrl } from './session-state';
import { sensorAssistedRow } from './smart-layout';
import { useClockSync } from './use-clock-sync';

const LayoutEditor = lazy(() =>
  import('./LayoutEditor').then(({ LayoutEditor: Component }) => ({ default: Component }))
);
const MosaicCanvas = lazy(() =>
  import('./MosaicCanvas').then(({ MosaicCanvas: Component }) => ({ default: Component }))
);
const QRPanel = lazy(() =>
  import('./QRPanel').then(({ QRPanel: Component }) => ({ default: Component }))
);

type MosaicSocket = Socket<ServerEvents, ClientEvents>;
type Mode = 'home' | 'pending' | 'controller' | 'display';

interface PendingBootstrap {
  status: 'pending';
  pairingCode: string;
  expiresAt: number;
}

interface RuntimeFullscreenDocument {
  fullscreenElement: Element | null;
  documentElement: { requestFullscreen?: () => Promise<void> };
  exitFullscreen?: () => Promise<void>;
}

export function App() {
  const socket = useMemo<MosaicSocket>(
    () => io({ autoConnect: false, transports: ['websocket', 'polling'] }),
    []
  );
  const initialInvite = useMemo(() => {
    const token = inviteTokenFromHash(window.location.hash);
    if (token) window.history.replaceState({}, '', '/');
    return token;
  }, []);
  const [room, setRoom] = useState<PublicRoomState | null>(null);
  const [mode, setMode] = useState<Mode>(initialInvite ? 'pending' : 'home');
  const [roomMode, setRoomMode] = useState<RoomMode>('private');
  const [pendingDevices, setPendingDevices] = useState<PendingDeviceSummary[]>([]);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [pendingExpiresAt, setPendingExpiresAt] = useState<number | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [controllerAuthorized, setControllerAuthorized] = useState(false);
  const [invite, setInvite] = useState<InviteState | null>(null);
  const [connection, setConnection] = useState<'connecting' | 'connected' | 'offline'>(
    'connecting'
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [identifyNotice, setIdentifyNotice] = useState<ScreenIdentify | null>(null);
  const [sensorStatus, setSensorStatus] = useState<OrientationSensorStatus>('idle');
  const [locale, setLocale] = useState<Locale>(readLocale);
  const modeRef = useRef<Mode>(initialInvite ? 'pending' : 'home');
  const controllerRef = useRef(false);
  const deviceIdRef = useRef<string | null>(null);
  const resumePreferenceRef = useRef<'controller' | 'display'>('display');
  const bootstrapRef = useRef<Promise<PendingBootstrap | null> | null>(null);
  const identifyTimer = useRef<number | null>(null);
  const sensorStop = useRef<(() => void) | null>(null);
  const lastOrientationSentAt = useRef(0);
  const clockOffset = useClockSync(socket, room !== null);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  useEffect(() => {
    controllerRef.current = controllerAuthorized;
  }, [controllerAuthorized]);
  useEffect(() => {
    deviceIdRef.current = deviceId;
  }, [deviceId]);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const applySession = useCallback((state: SessionResumeState) => {
    if (state.role === 'controller') {
      modeRef.current = 'controller';
      controllerRef.current = true;
      deviceIdRef.current = state.deviceId;
      setControllerAuthorized(true);
      setDeviceId(state.deviceId);
      setRoom(state.room);
      setRoomMode(state.room.mode);
      setPendingDevices(state.room.pendingDevices);
      setPairingCode(null);
      setPendingExpiresAt(null);
      setMode('controller');
      window.history.replaceState({}, '', '/?mode=controller');
      return;
    }
    controllerRef.current = false;
    setControllerAuthorized(false);
    setPendingDevices([]);
    setInvite(null);
    if (state.role === 'display') {
      modeRef.current = 'display';
      deviceIdRef.current = state.deviceId;
      setDeviceId(state.deviceId);
      setRoom(state.room);
      setPairingCode(null);
      setPendingExpiresAt(null);
      setMode('display');
      window.history.replaceState({}, '', '/');
      return;
    }
    deviceIdRef.current = null;
    modeRef.current = 'pending';
    setDeviceId(null);
    setRoom(null);
    setPairingCode(state.pairingCode);
    setPendingExpiresAt(state.expiresAt);
    setMode('pending');
    window.history.replaceState({}, '', '/');
  }, []);

  const resumeSession = useCallback(
    async (prefer: 'controller' | 'display', quiet = false) => {
      try {
        const state = await request<SessionResumeState>((ack) =>
          socket.emit('session:resume', { prefer, viewport: readViewport() }, ack)
        );
        applySession(state);
      } catch (reason) {
        if (!quiet) setError(messageFrom(reason, 'Could not restore this session.'));
        if (modeRef.current !== 'home' && modeRef.current !== 'pending') {
          modeRef.current = 'home';
          setRoom(null);
          setDeviceId(null);
          setControllerAuthorized(false);
          setMode('home');
        }
      }
    },
    [applySession, socket]
  );

  const reconnectAndResume = useCallback(
    (prefer: 'controller' | 'display') => {
      resumePreferenceRef.current = prefer;
      setConnection('connecting');
      socket.disconnect();
      socket.connect();
    },
    [socket]
  );

  const resetToHome = useCallback(() => {
    controllerRef.current = false;
    deviceIdRef.current = null;
    setIdentifyNotice(null);
    setError(null);
    setRoom(null);
    setDeviceId(null);
    setControllerAuthorized(false);
    setPairingCode(null);
    setPendingExpiresAt(null);
    setPendingDevices([]);
    setInvite(null);
    sensorStop.current?.();
    sensorStop.current = null;
    setSensorStatus('idle');
    modeRef.current = 'home';
    setMode('home');
    window.history.replaceState({}, '', '/');
    reconnectAndResume('display');
  }, [reconnectAndResume]);

  useEffect(() => {
    const onConnect = () => {
      setConnection('connected');
      void resumeSession(resumePreferenceRef.current, modeRef.current === 'home');
    };
    const onDisconnect = () => setConnection('offline');
    const onState = (next: PublicRoomState) => setRoom(next);
    const onControllerState = (next: ControllerRoomState) => {
      if (!controllerRef.current) return;
      setRoom(next);
      setRoomMode(next.mode);
      setPendingDevices(next.pendingDevices);
    };
    const onError = (message: string) => setError(message);
    const onInviteConsumed = () => setInvite(null);
    const onIdentify = (notice: ScreenIdentify) => {
      if (identifyTimer.current) window.clearTimeout(identifyTimer.current);
      setIdentifyNotice(notice);
      identifyTimer.current = window.setTimeout(
        () => setIdentifyNotice(null),
        Math.max(0, notice.expiresAt - Date.now())
      );
    };
    const onScreenRemoved = (notice: ScreenRemoved) => {
      if (notice.deviceId !== deviceIdRef.current) return;
      deviceIdRef.current = null;
      setDeviceId(null);
      setIdentifyNotice(null);
      if (controllerRef.current) {
        modeRef.current = 'controller';
        setMode('controller');
      } else {
        modeRef.current = 'home';
        setRoom(null);
        setMode('home');
        reconnectAndResume('display');
      }
    };
    const onScreenApproved = () => reconnectAndResume('display');
    const onScreenDenied = () => {
      modeRef.current = 'home';
      setPairingCode(null);
      setPendingExpiresAt(null);
      setMode('home');
      setError('The controller declined this screen.');
      void postSession('/api/sessions/leave', { role: 'device' }).finally(() =>
        reconnectAndResume('display')
      );
    };
    const onRoomClosed = (notice: RoomClosed) => {
      void postSession('/api/sessions/leave', { role: 'device' }).finally(() => {
        resetToHome();
        setError(`Lobby ${notice.code} was closed by its controller.`);
      });
    };
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('room:state', onState);
    socket.on('controller:state', onControllerState);
    socket.on('room:error', onError);
    socket.on('invite:consumed', onInviteConsumed);
    socket.on('screen:identify', onIdentify);
    socket.on('screen:removed', onScreenRemoved);
    socket.on('screen:approved', onScreenApproved);
    socket.on('screen:denied', onScreenDenied);
    socket.on('room:closed', onRoomClosed);

    let active = true;
    const start = async () => {
      try {
        if (!bootstrapRef.current)
          bootstrapRef.current = initialInvite
            ? postSession<PendingBootstrap>('/api/invites/exchange', {
                token: initialInvite,
                viewport: readViewport()
              })
            : Promise.resolve(null);
        const pending = await bootstrapRef.current;
        if (!active) return;
        if (pending) {
          modeRef.current = 'pending';
          setPairingCode(pending.pairingCode);
          setPendingExpiresAt(pending.expiresAt);
          setMode('pending');
        }
      } catch (reason) {
        if (active) {
          modeRef.current = 'home';
          setMode('home');
          setError(messageFrom(reason, 'Could not use this invitation.'));
        }
      } finally {
        if (active) socket.connect();
      }
    };
    void start();
    return () => {
      active = false;
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('room:state', onState);
      socket.off('controller:state', onControllerState);
      socket.off('room:error', onError);
      socket.off('invite:consumed', onInviteConsumed);
      socket.off('screen:identify', onIdentify);
      socket.off('screen:removed', onScreenRemoved);
      socket.off('screen:approved', onScreenApproved);
      socket.off('screen:denied', onScreenDenied);
      socket.off('room:closed', onRoomClosed);
      if (identifyTimer.current) window.clearTimeout(identifyTimer.current);
      socket.disconnect();
    };
  }, [initialInvite, reconnectAndResume, resetToHome, resumeSession, socket]);

  useEffect(
    () => () => {
      sensorStop.current?.();
    },
    []
  );

  const enableOrientation = useCallback(async () => {
    if (sensorStatus === 'active' || sensorStatus === 'requesting') return;
    setSensorStatus('requesting');
    setError(null);
    try {
      sensorStop.current?.();
      sensorStop.current = await startOrientationSensor((reading) => {
        const now = performance.now();
        if (now - lastOrientationSentAt.current < 250 || !socket.connected) return;
        lastOrientationSentAt.current = now;
        socket.emit('orientation:update', reading);
      });
      setSensorStatus('active');
    } catch (reason) {
      const message = messageFrom(reason, 'Could not start the orientation sensor.');
      setSensorStatus(message.includes('unavailable') ? 'unsupported' : 'denied');
      setError(message);
    }
  }, [sensorStatus, socket]);

  const createInvite = useCallback(async () => {
    try {
      const next = await request<InviteState>((ack) => socket.emit('invite:create', {}, ack));
      setInvite(next);
    } catch (reason) {
      setError(messageFrom(reason, 'Could not create an invitation.'));
    }
  }, [socket]);

  useEffect(() => {
    if (controllerAuthorized && connection === 'connected' && !invite) void createInvite();
  }, [connection, controllerAuthorized, createInvite, invite]);

  useEffect(() => {
    if (!invite) return;
    const timer = window.setTimeout(
      () => setInvite(null),
      Math.max(0, invite.expiresAt - Date.now())
    );
    return () => window.clearTimeout(timer);
  }, [invite]);

  useEffect(() => {
    if (mode !== 'pending' || !pendingExpiresAt) return;
    const timer = window.setTimeout(
      () => {
        modeRef.current = 'home';
        setMode('home');
        setPairingCode(null);
        setPendingExpiresAt(null);
        setError('This screen request expired. Request a new invitation or pairing code.');
        void postSession('/api/sessions/leave', { role: 'device' }).finally(() =>
          reconnectAndResume('display')
        );
      },
      Math.max(0, pendingExpiresAt - Date.now())
    );
    return () => window.clearTimeout(timer);
  }, [mode, pendingExpiresAt, reconnectAndResume]);

  useEffect(() => {
    if (!deviceId || !room?.devices.some((device) => device.id === deviceId) || !socket.connected)
      return;
    let timer = 0;
    const publishViewport = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(
        () => socket.emit('viewport:update', { viewport: readViewport() }),
        180
      );
    };
    window.addEventListener('resize', publishViewport);
    window.visualViewport?.addEventListener('resize', publishViewport);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', publishViewport);
      window.visualViewport?.removeEventListener('resize', publishViewport);
    };
  }, [deviceId, room, socket]);

  const createSession = async (nextMode: RoomMode, joinAsDisplay: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await postSession<ControllerRoomState>('/api/rooms', {
        mode: nextMode,
        viewport: readViewport(),
        joinAsDisplay
      });
      reconnectAndResume('controller');
    } catch (reason) {
      setError(messageFrom(reason, 'Could not create a mosaic.'));
    } finally {
      setBusy(false);
    }
  };

  const joinSession = async (code: string) => {
    const normalized = code.trim().toUpperCase();
    if (!normalized) return;
    setBusy(true);
    setError(null);
    try {
      const pending = await postSession<PendingBootstrap>('/api/join-requests', {
        code: normalized,
        viewport: readViewport()
      });
      setPairingCode(pending.pairingCode);
      setPendingExpiresAt(pending.expiresAt);
      modeRef.current = 'pending';
      setMode('pending');
      reconnectAndResume('display');
    } catch (reason) {
      setError(messageFrom(reason, 'Could not request access to this mosaic.'));
    } finally {
      setBusy(false);
    }
  };

  const invokeOwner = async (dispatch: (ack: Ack<PublicRoomState>) => void) => {
    if (!controllerAuthorized) return setError('This device is not the controller.');
    setError(null);
    try {
      const state = await request<PublicRoomState>((ack) => dispatch(ack));
      setRoom(state);
    } catch (reason) {
      setError(messageFrom(reason, 'The controller request failed.'));
    }
  };

  const updateLayout = (device: string, layout: TileLayout) =>
    void invokeOwner((ack) => socket.emit('layout:set', { deviceId: device, layout }, ack));
  const arrange = (kind: 'row' | 'grid' | 'sensors') => {
    if (!room) return;
    if (kind === 'sensors') {
      const layouts = sensorAssistedRow(room.devices);
      if (layouts.length) void invokeOwner((ack) => socket.emit('layouts:set', { layouts }, ack));
      return;
    }
    const gap = 24;
    const columns =
      kind === 'row' ? room.devices.length : Math.max(1, Math.ceil(Math.sqrt(room.devices.length)));
    let x = 0;
    let y = 0;
    let rowHeight = 0;
    const layouts = room.devices.map((device, index) => {
      if (index > 0 && index % columns === 0) {
        x = 0;
        y += rowHeight + gap;
        rowHeight = 0;
      }
      const layout = { ...device.layout, x, y, rotation: 0 };
      x += layout.width + gap;
      rowHeight = Math.max(rowHeight, layout.height);
      return { deviceId: device.id, layout };
    });
    void invokeOwner((ack) => socket.emit('layouts:set', { layouts }, ack));
  };
  const setScene = (name: SceneName) =>
    void invokeOwner((ack) => socket.emit('scene:set', { name }, ack));
  const setRunning = (running: boolean) =>
    void invokeOwner((ack) => socket.emit('scene:running', { running }, ack));
  const resetShow = () => void invokeOwner((ack) => socket.emit('scene:reset', {}, ack));
  const identifyScreen = (deviceId: string) =>
    void invokeOwner((ack) => socket.emit('screen:identify', { deviceId }, ack));
  const removeScreen = (deviceId: string) =>
    void invokeOwner((ack) => socket.emit('screen:remove', { deviceId }, ack));
  const approveScreen = (pendingId: string) =>
    void invokeControllerState((ack) => socket.emit('screen:approve', { pendingId }, ack));
  const denyScreen = (pendingId: string) =>
    void invokeControllerState((ack) => socket.emit('screen:deny', { pendingId }, ack));
  const invokeControllerState = async (dispatch: (ack: Ack<ControllerRoomState>) => void) => {
    if (!controllerAuthorized) return setError('This device is not the controller.');
    setError(null);
    try {
      const state = await request<ControllerRoomState>((ack) => dispatch(ack));
      setRoom(state);
      setRoomMode(state.mode);
      setPendingDevices(state.pendingDevices);
    } catch (reason) {
      setError(messageFrom(reason, 'The controller request failed.'));
    }
  };
  const showDisplay = () => {
    if (!deviceId) return setError('This controller is not registered as a display.');
    modeRef.current = 'display';
    setMode('display');
    window.history.replaceState({}, '', '/');
  };
  const leaveSession = async () => {
    const roles: Array<'controller' | 'device'> = [];
    if (controllerRef.current) roles.push('controller');
    if (deviceIdRef.current || modeRef.current === 'pending') roles.push('device');
    await Promise.all(
      roles.map((role) => postSession('/api/sessions/leave', { role }).catch(() => undefined))
    );
    resetToHome();
  };
  const deleteLobby = async () => {
    if (!window.confirm(`Close lobby ${room?.code ?? ''} for every connected screen?`)) return;
    setBusy(true);
    setError(null);
    try {
      await deleteSession<{ closed: true }>('/api/rooms/current');
      resetToHome();
    } catch (reason) {
      setError(messageFrom(reason, 'Could not close this lobby.'));
    } finally {
      setBusy(false);
    }
  };
  const chooseMedia = async (file: File | undefined) => {
    if (!file) return;
    const accepted = [
      'image/png',
      'image/jpeg',
      'image/webp',
      'image/gif',
      'video/mp4',
      'video/quicktime',
      'video/webm'
    ];
    if (!accepted.includes(file.type) || file.size > MAX_MEDIA_BYTES) {
      setError('Use PNG, JPEG, WebP, GIF, MP4, MOV, or WebM up to 50 MB.');
      return;
    }
    setMediaBusy(true);
    setError(null);
    try {
      const state = await uploadSessionFile<PublicRoomState>('/api/media', file);
      setRoom(state);
    } catch (reason) {
      setError(messageFrom(reason, 'The media file could not be uploaded.'));
    } finally {
      setMediaBusy(false);
    }
  };

  if (mode === 'pending') {
    return (
      <>
        <SiteControls locale={locale} onLocaleChange={setLocale} />
        <Pending
          locale={locale}
          connection={connection}
          error={error}
          pairingCode={pairingCode}
          onLeave={() => void leaveSession()}
        />
      </>
    );
  }

  if (!room || mode === 'home') {
    return (
      <>
        <SiteControls locale={locale} onLocaleChange={setLocale} />
        <Home
          locale={locale}
          busy={busy}
          connection={connection}
          error={error}
          joinCode={joinCode}
          onCreate={(nextMode, joinAsDisplay) => void createSession(nextMode, joinAsDisplay)}
          onJoin={() => void joinSession(joinCode)}
          onJoinCode={setJoinCode}
        />
      </>
    );
  }

  if (mode === 'display' || !controllerAuthorized) {
    if (!deviceId) return null;
    return (
      <>
        <SiteControls locale={locale} onLocaleChange={setLocale} />
        <Display
          room={room}
          deviceId={deviceId}
          clockOffset={clockOffset}
          connection={connection}
          identifyNotice={identifyNotice}
          sensorStatus={sensorStatus}
          onEnableOrientation={() => void enableOrientation()}
          onLeave={() => void leaveSession()}
        />
      </>
    );
  }

  return (
    <>
      <SiteControls locale={locale} onLocaleChange={setLocale} />
      <Controller
        room={room}
        deviceId={deviceId}
        roomMode={roomMode}
        pendingDevices={pendingDevices}
        invite={invite}
        clockOffset={clockOffset}
        connection={connection}
        error={error}
        mediaBusy={mediaBusy}
        sensorStatus={sensorStatus}
        onArrange={arrange}
        onLayout={updateLayout}
        onRunning={setRunning}
        onScene={setScene}
        onMedia={(file) => void chooseMedia(file)}
        onReset={resetShow}
        onIdentify={identifyScreen}
        onRemove={removeScreen}
        onApprove={approveScreen}
        onDeny={denyScreen}
        onRefreshInvite={() => void createInvite()}
        onEnableOrientation={() => void enableOrientation()}
        onLeave={() => void leaveSession()}
        onDelete={() => void deleteLobby()}
        onDisplay={showDisplay}
      />
    </>
  );
}

function messageFrom(reason: unknown, fallback: string): string {
  return reason instanceof Error ? reason.message : fallback;
}

function MosaicBotanicalBackdrop() {
  const group = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let frame = 0;
    const cleanups: Array<() => void> = [];
    const settled = Array.from(group.current?.querySelectorAll('img') ?? []).map(async (image) => {
      if (!image.complete) {
        await new Promise<void>((resolve) => {
          const finish = () => {
            image.removeEventListener('load', finish);
            image.removeEventListener('error', finish);
            resolve();
          };
          image.addEventListener('load', finish, { once: true });
          image.addEventListener('error', finish, { once: true });
          cleanups.push(finish);
        });
      }
      if (image.naturalWidth > 0) await image.decode().catch(() => {});
    });
    void Promise.all(settled).then(() => {
      if (cancelled) return;
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => {
          if (!cancelled) setReady(true);
        });
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      cleanups.forEach((cleanup) => cleanup());
    };
  }, []);

  return (
    <div
      className="mosaic-botanicals"
      data-botanical-ready={ready ? 'true' : 'false'}
      aria-hidden="true"
      ref={group}
    >
      {['left', 'right'].map((side) => (
        <img
          key={side}
          src={`/mosaic-botanical-${side}-v2.webp`}
          alt=""
          width="512"
          height="768"
          decoding="async"
          loading="eager"
        />
      ))}
    </div>
  );
}
function Home({
  locale,
  busy,
  connection,
  error,
  joinCode,
  onCreate,
  onJoin,
  onJoinCode
}: {
  locale: Locale;
  busy: boolean;
  connection: string;
  error: string | null;
  joinCode: string;
  onCreate: (mode: RoomMode, joinAsDisplay: boolean) => void;
  onJoin: () => void;
  onJoinCode: (value: string) => void;
}) {
  const [joinAsDisplay, setJoinAsDisplay] = useState(false);
  const copy = mosaicCopy[locale].home;
  return (
    <main className="landing">
      <MosaicMasthead locale={locale} connection={connection} />
      <MosaicBotanicalBackdrop />
      <section className="hero">
        <div className="hero-copy">
          <span className="eyebrow">{copy.eyebrow}</span>
          <h1>
            {copy.title[0]}
            <br />
            <em>{copy.title[1]}</em>
          </h1>
          <p>{copy.lead}</p>
        </div>
        <div className="home-card">
          <span className="eyebrow">{copy.desk}</span>
          <button
            className="primary"
            disabled={busy || connection !== 'connected'}
            onClick={() => onCreate('private', joinAsDisplay)}
          >
            {copy.create} <span>↗</span>
          </button>
          <button
            className="secondary demo-room"
            disabled={busy || connection !== 'connected'}
            type="button"
            onClick={() => onCreate('demo', true)}
          >
            {copy.demo}
          </button>
          <label className="participation-choice">
            <input
              checked={joinAsDisplay}
              type="checkbox"
              onChange={(event) => setJoinAsDisplay(event.target.checked)}
            />
            <span>
              {copy.include}
              <small>{copy.includeNote}</small>
            </span>
          </label>
          <div className="or">
            <span />
            {copy.orJoin}
            <span />
          </div>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onJoin();
            }}
          >
            <label htmlFor="room-code">{copy.joinLabel}</label>
            <div className="join-row">
              <input
                id="room-code"
                value={joinCode}
                maxLength={6}
                placeholder="AB12CD"
                onChange={(event) =>
                  onJoinCode(event.target.value.toUpperCase().replace(/[^A-HJ-NP-Z2-9]/g, ''))
                }
              />
              <button className="secondary" disabled={busy || joinCode.length !== 6}>
                {copy.join}
              </button>
            </div>
          </form>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
        </div>
      </section>
      <section className="steps" aria-label={copy.stepsLabel}>
        {copy.steps.map((step, index) => (
          <div key={step[0]}>
            <b>{String(index + 1).padStart(2, '0')}</b>
            <span>{step[0]}</span>
            <p>{step[1]}</p>
          </div>
        ))}
      </section>
    </main>
  );
}

function Pending({
  locale,
  connection,
  error,
  pairingCode,
  onLeave
}: {
  locale: Locale;
  connection: string;
  error: string | null;
  pairingCode: string | null;
  onLeave: () => void;
}) {
  const copy = mosaicCopy[locale].pending;
  return (
    <main className="landing pending-landing">
      <MosaicMasthead locale={locale} connection={connection} />
      <section className="pending-card">
        <span className="eyebrow">{copy.eyebrow}</span>
        <h1>{pairingCode ?? '····'}</h1>
        <p>{copy.lead}</p>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button className="secondary" type="button" onClick={onLeave}>
          {copy.cancel}
        </button>
      </section>
    </main>
  );
}

function Display({
  room,
  deviceId,
  clockOffset,
  connection,
  identifyNotice,
  sensorStatus,
  onEnableOrientation,
  onLeave
}: {
  room: PublicRoomState;
  deviceId: string;
  clockOffset: number;
  connection: string;
  identifyNotice: ScreenIdentify | null;
  sensorStatus: OrientationSensorStatus;
  onEnableOrientation: () => void;
  onLeave: () => void;
}) {
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
  const [controlsVisible, setControlsVisible] = useState(true);
  const [fullscreenError, setFullscreenError] = useState<string | null>(null);
  const fullscreenDocument = document as unknown as RuntimeFullscreenDocument;
  const fullscreenSupported = Boolean(
    fullscreenDocument.documentElement.requestFullscreen && fullscreenDocument.exitFullscreen
  );

  useEffect(() => {
    const updateFullscreen = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
      setFullscreenError(null);
    };
    document.addEventListener('fullscreenchange', updateFullscreen);
    return () => document.removeEventListener('fullscreenchange', updateFullscreen);
  }, []);

  const toggleFullscreen = () => {
    if (!fullscreenSupported) return;
    setFullscreenError(null);
    const enteringFullscreen = !fullscreenDocument.fullscreenElement;
    const action = fullscreenDocument.fullscreenElement
      ? fullscreenDocument.exitFullscreen?.()
      : fullscreenDocument.documentElement.requestFullscreen?.();
    if (!action) {
      setFullscreenError('Full screen is unavailable in this browser.');
      return;
    }
    void action
      .then(() => {
        if (enteringFullscreen && !document.fullscreenElement)
          setFullscreenError('Full screen is unavailable in this browser.');
      })
      .catch(() => setFullscreenError('Full screen is unavailable in this browser.'));
  };
  return (
    <main className="display-stage">
      <Suspense fallback={<div className="stage-canvas module-loading" aria-hidden="true" />}>
        <MosaicCanvas
          room={room}
          deviceId={deviceId}
          clockOffset={clockOffset}
          className="stage-canvas"
        />
      </Suspense>
      {identifyNotice?.deviceId === deviceId && (
        <div className="screen-identification" role="status">
          <span className="eyebrow">This screen</span>
          <strong>{room.devices.find((device) => device.id === deviceId)?.label}</strong>
          <p>Matched in the controller.</p>
        </div>
      )}
      {controlsVisible ? (
        <aside className="stage-controls" aria-label="Display controls">
          <span>
            {room.devices.find((device) => device.id === deviceId)?.label} · {connection}
          </span>
          {fullscreenSupported && (
            <button className="stage-control" type="button" onClick={toggleFullscreen}>
              {isFullscreen ? 'Exit full screen' : 'Full screen'}
            </button>
          )}
          {sensorStatus !== 'active' && sensorStatus !== 'unsupported' && (
            <button
              className="stage-control"
              disabled={sensorStatus === 'requesting'}
              type="button"
              onClick={onEnableOrientation}
            >
              {sensorStatus === 'requesting' ? 'Enabling…' : 'Enable orientation'}
            </button>
          )}
          <button className="stage-control" type="button" onClick={() => setControlsVisible(false)}>
            Hide controls
          </button>
          <button className="stage-control quiet" type="button" onClick={onLeave}>
            Leave
          </button>
          {fullscreenError && <span className="fullscreen-error">{fullscreenError}</span>}
        </aside>
      ) : (
        <button
          aria-label="Show display controls"
          className="stage-controls-reveal"
          type="button"
          onClick={() => setControlsVisible(true)}
        >
          ···
        </button>
      )}
      {!room.scene.running && (
        <div className="stage-idle">
          <span className="brand-mark">S—M</span>
          <span className="eyebrow">
            Connected · {room.code} · {connection}
          </span>
          <p>Waiting for the controller to begin.</p>
        </div>
      )}
    </main>
  );
}

function Controller({
  room,
  deviceId,
  roomMode,
  pendingDevices,
  invite,
  clockOffset,
  connection,
  error,
  mediaBusy,
  sensorStatus,
  onArrange,
  onLayout,
  onRunning,
  onScene,
  onMedia,
  onReset,
  onIdentify,
  onRemove,
  onApprove,
  onDeny,
  onRefreshInvite,
  onEnableOrientation,
  onLeave,
  onDelete,
  onDisplay
}: {
  room: PublicRoomState;
  deviceId: string | null;
  roomMode: RoomMode;
  pendingDevices: PendingDeviceSummary[];
  invite: InviteState | null;
  clockOffset: number;
  connection: string;
  error: string | null;
  mediaBusy: boolean;
  sensorStatus: OrientationSensorStatus;
  onArrange: (kind: 'row' | 'grid' | 'sensors') => void;
  onLayout: (deviceId: string, layout: TileLayout) => void;
  onRunning: (running: boolean) => void;
  onScene: (name: SceneName) => void;
  onMedia: (file: File | undefined) => void;
  onReset: () => void;
  onIdentify: (deviceId: string) => void;
  onRemove: (deviceId: string) => void;
  onApprove: (pendingId: string) => void;
  onDeny: (pendingId: string) => void;
  onRefreshInvite: () => void;
  onEnableOrientation: () => void;
  onLeave: () => void;
  onDelete: () => void;
  onDisplay: () => void;
}) {
  const configuredPublicUrl = import.meta.env.VITE_PUBLIC_URL?.trim();
  const inviteUrl = invite
    ? publicInviteUrl(invite.token, configuredPublicUrl || window.location.origin)
    : null;
  const invitationHost = inviteUrl ? new URL(inviteUrl).hostname : '';
  const invitationIsLocal = ['localhost', '127.0.0.1', '[::1]'].includes(invitationHost);
  return (
    <main className="controller">
      <header className="controller-header">
        <div className="brand">
          <span className="brand-mark">S—M</span>
          <span>Screen—Mosaic</span>
        </div>
        <div className="controller-meta">
          <span className={`connection ${connection}`}>{connection}</span>
          <span className="code-chip">{roomMode}</span>
          <span className="code-chip">{room.code}</span>
          <button className="ghost" disabled={!deviceId} onClick={onDisplay}>
            Open display
          </button>
          <button className="ghost" type="button" onClick={onLeave}>
            Leave session
          </button>
          <button className="ghost danger" type="button" onClick={onDelete}>
            Close lobby
          </button>
        </div>
      </header>
      <div className="controller-grid">
        <section className="command-panel">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Control desk · live composition</span>
              <h1>{room.devices.length} screens · one world</h1>
            </div>
            <span className={room.scene.running ? 'playing' : 'muted'}>
              {room.scene.running ? 'Live' : 'Paused'}
            </span>
          </div>
          <div className="preview-frame">
            {deviceId ? (
              <Suspense fallback={<div className="preview-empty">Preparing preview…</div>}>
                <MosaicCanvas
                  room={room}
                  deviceId={deviceId}
                  clockOffset={clockOffset}
                  className="preview-canvas"
                />
              </Suspense>
            ) : (
              <div className="preview-empty">
                Join this controller as a screen to preview its crop.
              </div>
            )}
          </div>
          <div className="scene-picker">
            <span className="eyebrow">Programme · choose material</span>
            <div className="scene-buttons">
              {(['orb', 'scanner', 'media'] as const).map((name) => (
                <button
                  key={name}
                  className={room.scene.name === name ? 'scene active' : 'scene'}
                  disabled={name === 'media' && !room.scene.media}
                  onClick={() => onScene(name)}
                >
                  {name === 'orb' ? 'Moving light' : name === 'scanner' ? 'Light scan' : 'My media'}
                </button>
              ))}
            </div>
            <label className={mediaBusy ? 'upload busy' : 'upload'}>
              <span>
                {mediaBusy
                  ? 'Uploading media…'
                  : room.scene.media
                    ? `Replace ${room.scene.media.name}`
                    : 'Choose a photo, GIF, or video · 50 MB max'}
              </span>
              <input
                disabled={mediaBusy}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
                onChange={(event) => {
                  onMedia(event.target.files?.[0]);
                  event.currentTarget.value = '';
                }}
              />
            </label>
            {room.scene.media && (
              <div className="media-summary">
                <span>{room.scene.media.kind === 'video' ? 'VIDEO' : 'IMAGE'}</span>
                <strong>{room.scene.media.name}</strong>
                <small>
                  {formatBytes(room.scene.media.size)} · videos play muted · MP4/H.264 recommended
                  across platforms
                </small>
              </div>
            )}
          </div>
          <div className="playback">
            <button
              className="primary"
              disabled={mediaBusy}
              onClick={() => onRunning(!room.scene.running)}
            >
              {room.scene.running ? 'Stop scene' : 'Start scene'}{' '}
              <span>{room.scene.running ? '■' : '▶'}</span>
            </button>
            <p>Starts 850 ms ahead using the server clock; each screen renders locally.</p>
            <button className="ghost" type="button" onClick={onReset}>
              Finish show &amp; reset
            </button>
          </div>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
        </section>
        <aside className="invite-column">
          {inviteUrl ? (
            <Suspense fallback={<div className="invite-loading">Preparing code…</div>}>
              <QRPanel url={inviteUrl} />
            </Suspense>
          ) : (
            <div className="invite-loading">Preparing a one-time invitation…</div>
          )}
          <div className="url-copy">
            <span className="eyebrow">One-time invitation</span>
            {inviteUrl && <code>{inviteUrl}</code>}
            <p>
              {invitationIsLocal
                ? 'This address opens only on this device. For phones, set VITE_PUBLIC_URL to the named public address before building.'
                : 'Expires after two minutes or one use. The fragment is removed before the screen connects.'}
            </p>
            <button className="ghost invite-refresh" type="button" onClick={onRefreshInvite}>
              Replace invitation
            </button>
            {inviteUrl && <ShareInviteButton url={inviteUrl} />}
          </div>
          {pendingDevices.length > 0 && (
            <div className="pending-list">
              <span className="eyebrow">Awaiting approval</span>
              {pendingDevices.map((pending) => (
                <div className="pending-row" key={pending.id}>
                  <span>
                    <strong>{pending.pairingCode}</strong>
                    <small>{pending.source === 'code' ? 'Pairing code' : 'Invitation'}</small>
                  </span>
                  <span className="device-actions">
                    <button
                      className="device-identify"
                      type="button"
                      onClick={() => onApprove(pending.id)}
                    >
                      Approve
                    </button>
                    <button
                      className="device-remove"
                      type="button"
                      onClick={() => onDeny(pending.id)}
                    >
                      Deny
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="device-list">
            <span className="eyebrow">Screens</span>
            {room.devices.map((device) => (
              <div className="device-row" key={device.id}>
                <span className={device.connected ? 'dot online' : 'dot'} />
                <span className="device-name">
                  {device.label}
                  <small>
                    {device.viewport.width} × {device.viewport.height}
                    {device.orientation
                      ? ` · angle ${Math.round(device.orientation.heading)}° ready`
                      : ' · angle not enabled'}
                  </small>
                </span>
                <span className="device-actions">
                  <button
                    className="device-identify"
                    type="button"
                    onClick={() => onIdentify(device.id)}
                  >
                    Find
                  </button>
                  <button
                    className="device-remove"
                    type="button"
                    onClick={() => onRemove(device.id)}
                  >
                    Remove
                  </button>
                </span>
              </div>
            ))}
          </div>
        </aside>
      </div>
      <section className="calibration-tools">
        <div className="arrange-buttons">
          <span className="eyebrow">Placement · quick tools</span>
          <button className="ghost" onClick={() => onArrange('row')}>
            Arrange row
          </button>
          <button className="ghost" onClick={() => onArrange('grid')}>
            Arrange grid
          </button>
          <button
            className="ghost sensor-arrange"
            disabled={!room.devices.some((device) => device.orientation)}
            onClick={() => onArrange('sensors')}
          >
            Place from device angles
          </button>
        </div>
        <p>
          {room.devices.filter((device) => device.orientation).length}/{room.devices.length} sensors
          ready. Sensors set physical angles; drag the tiles to match the exact gaps.
        </p>
        {deviceId && sensorStatus !== 'active' && sensorStatus !== 'unsupported' && (
          <button
            className="ghost"
            disabled={sensorStatus === 'requesting'}
            type="button"
            onClick={onEnableOrientation}
          >
            {sensorStatus === 'requesting' ? 'Enabling…' : 'Enable this device sensor'}
          </button>
        )}
      </section>
      <Suspense fallback={<div className="layout-editor invite-loading">Preparing layout…</div>}>
        <LayoutEditor
          key={room.devices.map((device) => device.id).join(':')}
          devices={room.devices}
          onCommit={onLayout}
        />
      </Suspense>
    </main>
  );
}

function ShareInviteButton({ url }: { url: string }) {
  const [label, setLabel] = useState('Share to a nearby device');
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Join my ScreenMosaic',
          text: 'Open this invitation on the screen you want to add.',
          url
        });
        setLabel('Invitation shared');
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setLabel('Link copied');
      } else {
        throw new Error('Sharing is unavailable.');
      }
    } catch (reason) {
      if (reason instanceof DOMException && reason.name === 'AbortError') return;
      setLabel('Could not share · copy the link above');
    }
    window.setTimeout(() => setLabel('Share to a nearby device'), 2_500);
  };
  return (
    <button className="ghost share-invite" type="button" onClick={() => void share()}>
      {label}
    </button>
  );
}

function formatBytes(size: number): string {
  if (size < 1_000_000) return `${Math.max(1, Math.round(size / 1_000))} KB`;
  return `${(size / 1_000_000).toFixed(1)} MB`;
}
